import { useOrderInteractions, useSubAccounts } from '@src/providers';
import { useDisplaySettings, useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { OrderStrategiesEnum } from '@src/tokens/orderStrategy';
import { isOrderTypeLimitAllIn } from '@src/utils/security';
import {
  isMultileg,
  type MarketDataCardUnifiedLiquidityMarket,
  MixpanelEvent,
  MixpanelEventProperty,
  OrdTypeEnum,
  type Security,
  UnifiedLiquidityEnum,
  useMarketAccountsContext,
  useMixpanel,
  useSecurity,
  useUnifiedLiquidity,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useDefaultSubAccount, useMaxOrderSizeDialogs } from '../../hooks';
import { useGetOrderPricingMode } from '../../hooks/useGetOrderPricingMode';
import type { LegParams } from '../../providers/OMSContext.types';
import { useOrderPresets } from '../../providers/OrderPresetsContext';
import { startOMSFormTiming } from '../OMS/NewOrder/OMSFormTiming';
import { orderSliceActions } from '../OMS/NewOrder/OrderSlice';
import { openView } from '../OMS/OMSSlice';
import { OMSView } from '../OMS/OMSView';
import { getNormalizedOrderQty } from '../OMS/useGetOrderCurrency';
import { useGetOrderStrategy } from '../OMS/useGetOrderStrategy';
import type { ClickToTradePreviewOrder, OnClickPriceArgs } from './types';
import { usePrepareOrder } from './usePrepareOrder';

interface UseHandleClickPriceProps {
  symbol: string | undefined;
  unifiedLiquidity: UnifiedLiquidityEnum | undefined;
  subAccount: string | undefined;
  defaultSubAccountId: number | undefined;
  markets: string[];
  marketAccounts: string[];
  unifiedLiquidityMarketSymbols?: MarketDataCardUnifiedLiquidityMarket[];
  aggregation: string | undefined;
  useOrderMarketDefaults?: boolean;
  orderPresetID: string | undefined;
}

export const useHandleClickMarketPrice = ({
  symbol,
  unifiedLiquidity,
  subAccount,
  defaultSubAccountId,
  marketAccounts,
  unifiedLiquidityMarketSymbols,
  markets,
  aggregation,
  useOrderMarketDefaults = false,
  orderPresetID,
}: UseHandleClickPriceProps) => {
  const { defaultSubAccount, defaultClickToTradeSubAccount } = useDefaultSubAccount(defaultSubAccountId);
  const security: Security | undefined = useSecurity(symbol);
  const [clickToTradePreviewOrder, setClickToTradePreviewOrder] = useState<ClickToTradePreviewOrder>();
  const [pendingClickToTradeValues, setPendingClickToTradeValues] = useState<OnClickPriceArgs['values']>();
  const { isMarketAccountActive, marketAccountsByName } = useMarketAccountsContext();
  const mixpanel = useMixpanel();
  const { send } = useOrderInteractions();
  const dispatch = useAppStateDispatch();
  const isOrderFormOpen = useAppStateSelector(state => state.OMS.openedView === OMSView.NewOrderForm);
  const { getOrderStrategy } = useGetOrderStrategy(security);
  const { getOrderPricingMode } = useGetOrderPricingMode(security);
  const { showAllInPrices } = useDisplaySettings();
  const { subAccountsByName } = useSubAccounts();
  const { clickToTradeAll, enableDefaultMarketCardSubAccount } = useTradingSettings();
  const { tradableSubAccounts } = useSubAccounts();
  const { orderPresetsByID } = useOrderPresets();
  const orderPreset = orderPresetID == null ? undefined : orderPresetsByID.get(orderPresetID);
  const ulLegSymbol = useUnifiedLiquidity(symbol)?.Tokens.at(0)?.Symbol;
  const marketCardSubAccount =
    !enableDefaultMarketCardSubAccount || subAccount == null ? undefined : subAccountsByName.get(subAccount);

  const prepareOrder = usePrepareOrder({
    security,
    selectedMarketAccounts: marketAccounts,
    selectedMarkets: markets,
    unifiedLiquidity,
    unifiedLiquidityMarketSymbols,
    selectedAggregation: aggregation,
  });

  const sendClickToTradeOrder = useCallback(
    (values: OnClickPriceArgs['values']) => {
      if (clickToTradePreviewOrder == null || security == null) {
        return;
      }

      const order = prepareOrder({
        ...clickToTradePreviewOrder,
        expectedFillPrice: values.expectedFillPrice,
        expectedFillQty: values.expectedFillQty,
        strategyName: isMultileg(security)
          ? unifiedLiquidity === UnifiedLiquidityEnum.Enabled
            ? OrderStrategiesEnum.Limit
            : OrderStrategiesEnum.MultilegAggressiveLimit
          : OrderStrategiesEnum.Limit, // For click to trade - we always send using LIMIT Strategy,
        defaultSubAccount: defaultClickToTradeSubAccount,
        marketCardSubAccount,
      });

      if (order == null) {
        return;
      }
      mixpanel.track(MixpanelEvent.SendOrder, {
        [MixpanelEventProperty.Source]: clickToTradePreviewOrder.source,
        [MixpanelEventProperty.Strategy]: order.selectedStrategy.Name,
      });

      // if using CTC with a multileg with UL leg we have to pass markets of the UL leg
      // so BE sends orders to the right market
      const legParams =
        ulLegSymbol && isMultileg(security) && unifiedLiquidity === UnifiedLiquidityEnum.Enabled
          ? [
              {
                Symbol: ulLegSymbol,
                Markets: order.ulLegMarkets ?? [],
                Initiating: false,
                LegIndex: undefined,
              } satisfies LegParams,
            ]
          : undefined;

      send({
        ...order,
        subAccount: marketCardSubAccount?.Name || defaultClickToTradeSubAccount?.Name,
        subAccounts: tradableSubAccounts,
        useTradeAllocations: false,
        legParams,
      });
      setClickToTradePreviewOrder(undefined);
      setPendingClickToTradeValues(undefined);

      // after submitting click to trade order we should not continue to prime and should just open cards
      dispatch(openView(OMSView.Cards));
    },
    [
      clickToTradePreviewOrder,
      security,
      prepareOrder,
      unifiedLiquidity,
      defaultClickToTradeSubAccount,
      marketCardSubAccount,
      mixpanel,
      ulLegSymbol,
      send,
      tradableSubAccounts,
      setClickToTradePreviewOrder,
      setPendingClickToTradeValues,
      dispatch,
    ]
  );

  const confirmPendingClickToTradeOrder = useCallback(() => {
    if (pendingClickToTradeValues == null) {
      return;
    }

    sendClickToTradeOrder(pendingClickToTradeValues);
  }, [pendingClickToTradeValues, sendClickToTradeOrder]);

  const maxOrderSizeDialogs = useMaxOrderSizeDialogs({
    onConfirmWarning: confirmPendingClickToTradeOrder,
    currency: clickToTradePreviewOrder?.orderCurrency,
    symbol: security?.Symbol,
    security,
  });

  const onClickPrice = useCallback(
    ({
      values,
      source,
      unifiedLiquidityMarketSymbols: clickUnifiedLiquidityMarketSymbols,
      activateOrder = true,
    }: OnClickPriceArgs) => {
      // normalize Qty based on initial order currency
      const orderQty = getNormalizedOrderQty({
        security,
        orderCurrency: values.currency,
        size: values.orderQty,
      });

      if (clickToTradeAll && activateOrder) {
        if (clickToTradePreviewOrder == null) {
          setPendingClickToTradeValues(undefined);
          setClickToTradePreviewOrder({
            ...values,
            orderQty,
            orderCurrency: values.currency,
            source,
            ordType: isOrderTypeLimitAllIn(showAllInPrices, security) ? OrdTypeEnum.LimitAllIn : OrdTypeEnum.Limit,
            strategyName: getOrderStrategy(),
            pricingMode: getOrderPricingMode(),
            unifiedLiquidityMarketSymbols: clickUnifiedLiquidityMarketSymbols,
          });
        } else if (clickToTradePreviewOrder.intent === values.intent && clickToTradePreviewOrder.side === values.side) {
          setPendingClickToTradeValues(values);
          const validationResult = maxOrderSizeDialogs.validate({ size: orderQty });
          if (validationResult !== true) {
            return;
          }

          sendClickToTradeOrder(values);
          return;
        } else {
          // if the order is not valid (or cancelled), we should clear the active order
          setClickToTradePreviewOrder(undefined);
          setPendingClickToTradeValues(undefined);
        }
      }

      if (clickToTradePreviewOrder == null) {
        const order = prepareOrder({
          ...values,
          orderQty,
          orderCurrency: values.currency,
          orderPreset,
          strategyName: getOrderStrategy(marketAccounts),
          defaultSubAccount,
          marketCardSubAccount,
          unifiedLiquidityMarketSymbols: clickUnifiedLiquidityMarketSymbols,
        });

        if (order == null) {
          return;
        }

        const activeMarketAccounts = order?.marketAccounts.filter(ma =>
          isMarketAccountActive(marketAccountsByName.get(ma))
        );
        if (order.marketAccounts.length !== activeMarketAccounts.length) {
          order.marketAccounts = activeMarketAccounts;
        }

        mixpanel.track(MixpanelEvent.PrimeOrder, {
          [MixpanelEventProperty.Intent]: values.intent,
          [MixpanelEventProperty.Aggregation]: orderPreset?.aggregation,
          [MixpanelEventProperty.SubAccount]: order.subAccountAllocations?.[0]?.subAccount,
        });

        if (!isOrderFormOpen) {
          startOMSFormTiming(OMSView.NewOrderForm);
        }
        dispatch(openView(OMSView.NewOrderForm));
        dispatch(
          orderSliceActions.primeNewOrderForm(useOrderMarketDefaults ? { ...order, marketAccounts: undefined } : order)
        );
      }
    },
    [
      security,
      clickToTradeAll,
      clickToTradePreviewOrder,
      maxOrderSizeDialogs,
      showAllInPrices,
      getOrderStrategy,
      getOrderPricingMode,
      setClickToTradePreviewOrder,
      setPendingClickToTradeValues,
      prepareOrder,
      marketCardSubAccount,
      mixpanel,
      orderPreset,
      marketAccounts,
      useOrderMarketDefaults,
      defaultSubAccount,
      isMarketAccountActive,
      marketAccountsByName,
      isOrderFormOpen,
      sendClickToTradeOrder,
      dispatch,
    ]
  );

  const onCancelActive = useCallback(() => {
    if (!maxOrderSizeDialogs.warnDialog.isOpen) {
      setPendingClickToTradeValues(undefined);
      setClickToTradePreviewOrder(undefined);
    }
  }, [maxOrderSizeDialogs.warnDialog.isOpen, setClickToTradePreviewOrder, setPendingClickToTradeValues]);

  return { clickToTradePreviewOrder, onClickPrice, onCancelActive, maxOrderSizeDialogs };
};
